/**
 * Owner Links & Reports Page
 * 
 * Purpose:
 * Displays property-specific external links (reports, contracts, financial documents) and global help
 * content organized into categorized sections. Provides two main sections:
 * 1. Reports: Grid of category-based links (Annual Maintenance Plan, CLC Contract, DBPR, Finances,
 *    Leases, Onboarding Info, Reports) with icons. Missing links show "Coming Soon" placeholders.
 * 2. Help & Insights: Grouped help files organized into sections (Start Here, Understanding Your Money,
 *    Operations & Ownership, Strategy & Learning, Additional Resources) with content type icons.
 * 
 * High-level Workflow:
 * 1. On mount: Component initializes with empty state, waits for property selection from SessionContext.
 * 2. Property selection: When selectedProperty changes, extracts listing_id and triggers fetch of
 *    property links from /api/v1/owner/links endpoint. Fetch is gated by propertiesLoading and
 *    selectedProperty availability to prevent race conditions.
 * 3. Data fetching: Fetches links via getPropertyLinks({ listingId }) with AbortController for
 *    cancellation on property switch. Handles multiple response shapes (array, { data: { links } },
 *    { links }) with fallback normalization.
 * 4. Data processing: Extracts global help files (scope === 'global_help') from fetched links.
 *    Builds normalized category-to-link Map for efficient lookup (handles whitespace/newline
 *    mismatches in category names).
 * 5. Rendering: Displays Reports section with category cards (clickable if URL exists, "Coming Soon"
 *    if missing). Displays Help & Insights section with grouped help files using HelpInsightsContent
 *    nested component.
 * 6. User interactions: Clicking report cards opens links in new tab. Help file cards open content
 *    URLs. Hover effects use inline DOM manipulation (candidate for CSS :hover in future phase).
 * 
 * Assumptions & Invariants:
 * - API Response Shapes: API responses may be wrapped ({ data: { links: [...] }, outcome: 'success' }),
 *   flat ({ links: [...] }), or direct array ([...]). Response normalization handles all formats
 *   with fallback to empty array if links not found.
 * - Category Name Matching: Category names from API may have whitespace/newline differences (e.g.,
 *   "CLC\nContract" vs "CLC Contract"). normalizeNameForMatching() handles this by collapsing all
 *   whitespace to single spaces and lowercasing for comparison. Original names are preserved for display.
 * - Owner-Safe View: API endpoint /api/v1/owner/links is owner-scoped and returns owner-safe data.
 *   Server is source of truth for RBAC. Client-side sanitization is not currently applied but could
 *   be added as defense-in-depth (see audit findings).
 * - Property Access Control: Fetch is gated by selectedProperty?.listing_id. If listingId is missing
 *   or propertiesLoading is true, fetch is blocked. This prevents fetching data for wrong property
 *   or before auth is ready.
 * - Abort Behavior: AbortController cancels in-flight requests on property switch or unmount.
 *   AbortError is silently ignored. State updates are guarded by abort checks before and after
 *   async operations to prevent stale updates.
 * - Help File Grouping: groupHelpFiles() uses name/description heuristics to categorize files.
 *   Heuristics are based on string includes() checks. Future extension: replace with backend
 *   tags/categories when available.
 * - Empty States: Both sections show appropriate empty states (loading skeletons, "Content coming
 *   soon") when data is unavailable.
 * 
 * Dependencies:
 * - Internal:
 *   - contexts/SessionContext.jsx: Authentication, property selection (effectiveMode, selectedProperty,
 *     propertiesLoading)
 *   - components/shared/SkeletonLoader.jsx: Loading skeleton components (SkeletonCard, SkeletonText)
 *   - components/owner/OwnerPageChrome.jsx: Page layout wrapper with property hero/thumb images
 *   - components/owner/HelpInsightsContent.jsx: Help & Insights content component (renders grouped help files)
 *   - services/ownerApi.js: API client (getPropertyLinks function)
 *   - utils/linksNormalization.js: normalizeNameForMatching() for category name matching
 *   - utils/sanitizeForOwnerView.js: Data sanitization for owner view (defense-in-depth RBAC)
 *   - styles/ownerLinksReportsPageStyles.js: Style definitions for page and component rendering
 *   - styles/ownerLinksReportsPageHover.css: CSS :hover and :focus-visible styles for interactive cards
 *   - utils/helpFilesGrouping.js: groupHelpFiles() and getContentTypeIcon() for help file categorization
 * - External:
 *   - react: React hooks (useState, useEffect, useRef, useMemo)
 *   - lucide-react: Icon components (Wrench, FileText, Building2, DollarSign, FileCheck, BookOpen,
 *     BarChart3)
 * 
 * Version: 1.0.0
 * 
 * Change Log:
 * - 2026-01-26: Phase 0 documentation - Added comprehensive charter-compliant file header with
 *   purpose, workflow, assumptions, invariants, dependencies, and change log. Added intent-level
 *   comments to utility functions, nested component, and key logic sections. No behavior changes.
 * - 2026-01-26: Phase 1 utility extraction - Extracted normalizeNameForMatching() to
 *   utils/linksNormalization.js and groupHelpFiles()/getContentTypeIcon() to utils/helpFilesGrouping.js.
 *   Functions moved verbatim with no logic changes. Updated imports to use extracted utilities.
 *   Removed unused lucide-react imports (File, Video, Headphones) as they are now in helpFilesGrouping.js.
 * - 2026-01-26: Phase 2 component extraction - Extracted HelpInsightsContent nested component to
 *   components/owner/HelpInsightsContent.jsx. Component moved verbatim with no logic changes. Now accepts
 *   helpFiles and styles as props. Updated imports and call site to use extracted component.
 *   Removed unused lucide-react imports (ExternalLink, Sparkles) as they are now in HelpInsightsContent.jsx.
 * - 2026-01-26: Phase 3 styles extraction - Extracted styles object to styles/ownerLinksReportsPageStyles.js.
 *   Styles moved verbatim with no value or key changes. Updated imports to use extracted styles module.
 *   Styles object passed as prop to HelpInsightsContent component (unchanged pattern).
 * - 2026-01-26: Phase 4 memoization - Added useMemo to linksByCategory Map construction in
 *   OwnerLinksReportsPage.jsx (dependencies: [links]) and to groupHelpFiles() result in
 *   HelpInsightsContent.jsx (dependencies: [helpFiles]). Logic moved verbatim into useMemo callbacks.
 *   No behavior changes, only performance optimization to avoid unnecessary recomputation.
 * - 2026-01-26: Phase 5 data sanitization & RBAC hardening - Added sanitizeForOwnerView() to sanitize
 *   links and helpFiles data before setting state. Server is source of truth for RBAC, but client-side
 *   sanitization provides defense-in-depth by removing admin-only fields (internal_*, guest_* except
 *   guest_name/guest_count, confidence, admin_* fields). effectiveMode drives sanitization logic
 *   (sanitizes in 'owner' or 'owner_view' mode). No UI behavior changes, only removes unsafe fields
 *   if they exist in the response.
 * - 2026-01-26: Phase 6 useEffect dependency optimization - Removed selectedProperty object from
 *   dependency array, keeping only selectedProperty?.listing_id and propertiesLoading primitives.
 *   This prevents unnecessary refetches when selectedProperty object reference changes but listing_id
 *   is unchanged. Guard logic updated to use listingId primitive directly. Abort logic preserved
 *   (still checks selectedProperty?.listing_id in async callbacks). No behavior changes, only
 *   reduces unnecessary refetches due to object reference churn.
 * - 2026-01-26: Phase 7 CSS hover replacement - Replaced inline onMouseEnter/onMouseLeave DOM
 *   manipulation with CSS :hover and :focus-visible rules in ownerLinksReportsPageHover.css.
 *   Added className="olrp-link-card" to report category cards and className="olrp-help-card" to
 *   help file cards. Removed all hover event handlers. Preserves identical visual behavior and
 *   transitions. No data fetching, RBAC, sanitization, memoization, or rendering logic changes.
 */
import { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useSession } from '../../contexts/SessionContext.jsx';
import { useViewport } from '../../contexts/ViewportContext.jsx';
import { SkeletonCard } from '../../components/shared/SkeletonLoader.jsx';
import OwnerPageChrome from '../../components/owner/OwnerPageChrome.jsx';
import {
  getPropertyLinks,
  getOwnerDropboxList,
  getOwnerDropboxFileBlob,
  getOwnerDropboxFileUrl,
  getOwnerHelpInsightsList,
  getOwnerHelpInsightsFileBlob,
  getOwnerHelpInsightsFileUrl,
  getOwnerHelpInsightsThumbnailUrl,
} from '../../services/ownerApi.js';
import { normalizeNameForMatching } from '../../utils/linksNormalization.js';
import { sanitizeForOwnerView } from '../../utils/sanitizeForOwnerView.js';
import { styles } from '../../styles/ownerLinksReportsPageStyles.js';
import '../../styles/ownerLinksReportsPageHover.css';
import DropboxBrowser from '../../modules/clc-properties/DropboxBrowser.jsx';
import VideoGuidesCarousel from '../../components/owner/VideoGuidesCarousel.jsx';
import {
  Wrench,
  FileText,
  Building2,
  DollarSign,
  FileCheck,
  BookOpen,
  BarChart3,
  Receipt,
  Folder,
  FolderOpen,
  X
} from 'lucide-react';

// Canonical Dropbox category names (matching Dropbox folder names)
const DROPBOX_CATEGORIES = [
  'Annual Maintenance Plan',
  'CLC Contract',
  'DBPR',
  'Finances',
  'Taxes',
  'Leases',
  'Onboarding Info',
  'Reports',
];

// Icon mapping for each category
const CATEGORY_ICONS = {
  'Annual Maintenance Plan': Wrench,
  'CLC Contract': FileText,
  'DBPR': Building2,
  'Finances': DollarSign,
  'Taxes': Receipt,
  'Leases': FileCheck,
  'Onboarding Info': BookOpen,
  'Reports': BarChart3,
};

export default function OwnerLinksReportsPage({ onLogout }) {
  const { effectiveMode, selectedProperty, propertiesLoading } = useSession();
  const { isMobile } = useViewport();
  const [links, setLinks] = useState([]);
  const [linksLoading, setLinksLoading] = useState(false);
  const [linksError, setLinksError] = useState(null);
  const [dropboxRoot, setDropboxRoot] = useState(null);   // null | { configured, root, entries }
  const [dropboxLoading, setDropboxLoading] = useState(false);
  const [browserFolder, setBrowserFolder] = useState(null); // null | { name, rel_path }

  // Track in-flight fetch to abort on property switch
  const abortControllerRef = useRef(null);

  // Stable data-source for the reusable DropboxBrowser (owner endpoints).
  const ownerDropboxApi = useMemo(() => {
    const listing_id = selectedProperty?.listing_id;
    if (!listing_id) return null;
    return {
      list: ({ path, cursor, signal } = {}) => getOwnerDropboxList({ listing_id, path, cursor, signal }),
      fileBlob: (relPath, { download, signal } = {}) => getOwnerDropboxFileBlob(listing_id, relPath, { download, signal }),
      fileUrl: (relPath) => getOwnerDropboxFileUrl(listing_id, relPath),
    };
  }, [selectedProperty?.listing_id]);

  // Data-source for the shared Help & Insights library — one global Dropbox folder,
  // identical for every owner (no per-property scoping). Stable across property switches.
  const helpInsightsApi = useMemo(() => ({
    list: ({ path, cursor, signal } = {}) => getOwnerHelpInsightsList({ path, cursor, signal }),
    fileBlob: (relPath, { download, signal } = {}) => getOwnerHelpInsightsFileBlob(relPath, { download, signal }),
    fileUrl: (relPath) => getOwnerHelpInsightsFileUrl(relPath),
    thumbUrl: (relPath) => getOwnerHelpInsightsThumbnailUrl(relPath),
  }), []);

  // Get property short name (matching Calendar/Reservations/Financials pattern)
  // Priority: clc_short_name > name > property_name > fallback
  const propertyShortName = selectedProperty?.clc_short_name || 
                            selectedProperty?.name || 
                            selectedProperty?.property_name || 
                            'Property';

  // Fetch property links when property changes
  // Phase 6: Optimized dependencies - depend only on listing_id primitive to avoid unnecessary
  // refetches when selectedProperty object reference changes but listing_id is unchanged.
  useEffect(() => {
    // Guard: Block fetch until properties are loaded and listing_id is available
    // Use listing_id primitive directly to avoid object reference churn
    const listingId = selectedProperty?.listing_id;
    if (!listingId || propertiesLoading) {
      if (propertiesLoading || !listingId) {
        // Clear links while loading or when listing_id is missing
        setLinks([]);
        setLinksError(null);
      }
      return;
    }

    // Abort any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this fetch
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // Track current listingId to prevent stale updates
    const currentListingId = listingId;

    const fetchLinks = async () => {
      setLinksLoading(true);
      setLinksError(null);

      try {
        const response = await getPropertyLinks({ listingId });
        
        // Check if fetch was aborted OR listingId changed BEFORE processing response
        if (abortController.signal.aborted || currentListingId !== selectedProperty?.listing_id) {
          return;
        }

        // Debug logging (only in development, guarded by URL param)
        const urlParams = new URLSearchParams(window.location.search);
        const debugLinks = urlParams.get('debug_links') === '1';
        
        // Extract links array - handle both response shapes
        // Response shape: { outcome: "success", data: { links: [...] } }
        // OR: { links: [...] } (if ownerFetch unwraps)
        let fetchedLinks = [];
        if (Array.isArray(response)) {
          fetchedLinks = response;
        } else if (response?.data?.links && Array.isArray(response.data.links)) {
          fetchedLinks = response.data.links;
        } else if (response?.links && Array.isArray(response.links)) {
          fetchedLinks = response.links;
        } else {
          // Fallback: try to find links anywhere in response
          fetchedLinks = response?.links || response?.data?.links || [];
        }

        if (debugLinks) {
          console.log('[OwnerLinksReportsPage] Response shape analysis:', {
            listingId,
            responseType: typeof response,
            isArray: Array.isArray(response),
            hasData: !!response?.data,
            hasLinks: !!response?.links,
            dataHasLinks: !!response?.data?.links,
            responseKeys: response ? Object.keys(response) : [],
            fetchedLinksCount: fetchedLinks.length,
            fetchedLinks: fetchedLinks.map(l => ({
              name: l?.name,
              normalized: l?.name ? normalizeNameForMatching(l.name) : null,
              url: l?.url,
              hasUrl: !!l?.url,
            })),
            expectedCategories: DROPBOX_CATEGORIES.map(c => ({
              original: c,
              normalized: normalizeNameForMatching(c),
            })),
          });
        }

        // Check if fetch was aborted OR listingId changed AFTER processing but BEFORE state update
        if (abortController.signal.aborted || currentListingId !== selectedProperty?.listing_id) {
          return;
        }

        // Defense-in-depth: Sanitize links data before setting state
        // Server is source of truth for RBAC, but client-side sanitization removes admin-only fields
        // (e.g., internal_*, guest_* except guest_name/guest_count, confidence, admin_* fields)
        // as defense-in-depth. effectiveMode drives sanitization: sanitize in 'owner' or 'owner_view' mode.
        const sanitizedLinks = sanitizeForOwnerView(fetchedLinks, effectiveMode);
        setLinks(sanitizedLinks);
        setLinksError(null);
        // Help & Insights is now sourced from a shared Dropbox folder (see helpInsightsApi),
        // no longer from global_help links.
      } catch (err) {
        // Ignore abort errors
        if (err.name === 'AbortError' || abortController.signal.aborted) {
          return;
        }

        // Check if fetch was aborted
        if (abortController.signal.aborted) {
          return;
        }

        console.error('[OwnerLinksReportsPage] Error fetching links:', err);
        setLinksError(err.message || 'Failed to fetch links');
        setLinks([]);
        setHelpFiles([]);
      } finally {
        if (!abortController.signal.aborted) {
          setLinksLoading(false);
        }
      }
    };

    fetchLinks();

    // Cleanup: abort fetch on unmount or property change
    return () => {
      abortController.abort();
    };
  }, [selectedProperty?.listing_id, propertiesLoading]);

  // Fetch the property's Dropbox root so the Reports tiles can be the real top-level
  // folders. On error or unconfigured, we fall back to the curated category tiles.
  useEffect(() => {
    const listingId = selectedProperty?.listing_id;
    if (!listingId || propertiesLoading) { setDropboxRoot(null); return; }
    const ac = new AbortController();
    setDropboxLoading(true);
    getOwnerDropboxList({ listing_id: listingId, path: '', signal: ac.signal })
      .then((res) => { if (!ac.signal.aborted) setDropboxRoot(res || null); })
      .catch(() => { if (!ac.signal.aborted) setDropboxRoot(null); })
      .finally(() => { if (!ac.signal.aborted) setDropboxLoading(false); });
    return () => ac.abort();
  }, [selectedProperty?.listing_id, propertiesLoading]);

  // Build a map of normalized category name -> link for quick lookup
  // Use normalized keys to handle whitespace/newline mismatches
  // Intent: Creates Map for O(1) category lookup during render. Memoized to avoid recomputation
  // on every render when links array hasn't changed. Normalized keys allow matching despite
  // whitespace/newline differences between API category names and DROPBOX_CATEGORIES.
  const linksByCategory = useMemo(() => {
    const map = new Map();
    links.forEach(link => {
      if (link.name) {
        const normalizedKey = normalizeNameForMatching(link.name);
        // Store with normalized key, but keep original link object (with original name for display)
        map.set(normalizedKey, link);
      }
    });
    return map;
  }, [links]);

  const dbxEntries = dropboxRoot?.entries || [];
  const folderEntries = dbxEntries.filter((e) => e.kind === 'folder');
  const rootFiles = dbxEntries.filter((e) => e.kind === 'file' && !String(e.name).startsWith('_'));
  const dropboxConfigured = !!dropboxRoot?.configured && folderEntries.length > 0;

  return (
    <OwnerPageChrome
      title={propertyShortName}
      subtitle="Links & Reports"
      heroUrl={selectedProperty?.hero_url}
      thumbUrl={selectedProperty?.thumb_url}
      compactLayout={isMobile}
    >
      {/* Reports Section */}
      <div style={styles.linksCard}>
        <h2 style={styles.sectionTitle}>Reports</h2>
        {((linksLoading || dropboxLoading) && !dropboxRoot) ? (
          <div style={styles.linksGrid}>
            {DROPBOX_CATEGORIES.map((category) => (
              <SkeletonCard key={category} style={styles.linkCard} />
            ))}
          </div>
        ) : dropboxConfigured ? (
          <>
            <div style={styles.linksGrid}>
              {folderEntries.map((folder) => {
                const IconComponent = CATEGORY_ICONS[folder.name] || FolderOpen;
                return (
                  <button
                    key={folder.rel_path}
                    type="button"
                    className="olrp-link-card"
                    style={{ ...styles.linkCard, border: 'none', cursor: 'pointer', font: 'inherit' }}
                    onClick={() => setBrowserFolder(folder)}
                  >
                    <div style={styles.linkIconContainer}>
                      <IconComponent size={28} strokeWidth={1.5} />
                    </div>
                    <div style={styles.linkTitle}>{folder.name}</div>
                  </button>
                );
              })}
            </div>
            {rootFiles.length > 0 && (
              <div style={{ marginTop: '1rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {rootFiles.map((f) => (
                  <button
                    key={f.rel_path}
                    type="button"
                    onClick={() => setBrowserFolder({ name: dropboxRoot.root, rel_path: '' })}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.4rem 0.7rem', border: '1px solid #e3e8ef', borderRadius: '999px', background: '#fff', cursor: 'pointer', fontSize: '0.85rem', color: '#334e68' }}
                  >
                    <FileText size={14} /> {f.name}
                  </button>
                ))}
              </div>
            )}
          </>
        ) : linksError ? (
          <div style={styles.errorMessage}>
            {linksError}
          </div>
        ) : (
          <div style={styles.linksGrid}>
            {DROPBOX_CATEGORIES.map((category) => {
              // Match using normalized names to handle whitespace/newline differences
              const normalizedCategory = normalizeNameForMatching(category);
              const link = linksByCategory.get(normalizedCategory);
              const hasUrl = link && link.url;
              const IconComponent = CATEGORY_ICONS[category] || FileText;
              
              if (hasUrl) {
                // Link exists - render clickable card
                return (
                  <a
                    key={category}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="olrp-link-card"
                    style={styles.linkCard}
                  >
                    <div style={styles.linkIconContainer}>
                      <IconComponent size={28} strokeWidth={1.5} />
                    </div>
                    <div style={styles.linkTitle}>{category}</div>
                  </a>
                );
              } else {
                // Link missing - render "Coming Soon" card (non-clickable)
                return (
                  <div
                    key={category}
                    style={styles.linkCardComingSoon}
                  >
                    <div style={styles.linkIconContainer}>
                      <IconComponent size={28} strokeWidth={1.5} style={{ opacity: 0.5 }} />
                    </div>
                    <div style={styles.linkTitle}>{category}</div>
                    <div style={styles.comingSoonText}>Coming Soon</div>
                  </div>
                );
              }
            })}
          </div>
        )}
      </div>

      {/* Dropbox folder browser (opens when a folder tile is clicked) — portaled to body */}
      {browserFolder && ownerDropboxApi && createPortal((
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setBrowserFolder(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem' }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: '12px', width: 'min(900px, 96vw)', maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.85rem 1.1rem', borderBottom: '1px solid #e3e8ef' }}>
              <FolderOpen size={18} />
              <span style={{ flex: 1, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {browserFolder.rel_path ? browserFolder.name : (dropboxRoot?.root || 'Files')}
              </span>
              <button type="button" onClick={() => setBrowserFolder(null)} title="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', display: 'inline-flex', padding: '0.25rem' }}>
                <X size={20} />
              </button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: '1rem 1.1rem' }}>
              <DropboxBrowser api={ownerDropboxApi} rootName={dropboxRoot?.root} initialPath={browserFolder.rel_path} />
            </div>
          </div>
        </div>
      ), document.body)}

      {/* Owner Portal Video Guides — horizontal video carousel (self-hides if empty) */}
      <VideoGuidesCarousel styles={styles} />

      {/* Help and Insights Section — shared Dropbox library, identical for every owner.
          Subfolders are the sections; navigate in to view PDFs/videos in the in-portal preview. */}
      <div style={styles.reportsCard}>
        <h2 style={styles.sectionTitle}>Help and Insights</h2>
        <div style={{ minHeight: 360, display: 'flex', flexDirection: 'column' }}>
          <DropboxBrowser api={helpInsightsApi} rootName="Help and Insights" tile />
        </div>
      </div>
    </OwnerPageChrome>
  );
}
